import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listHooksDataTable,
  listAccountHooksDataTable,
  getHookFilterOptions,
  getAccountHookFilterOptions,
} from '@kirin/api-client/api/hooks';
import type {
  HookInventoryRow,
  HookDataTableSortField,
  HookFilterOptionField,
  HookFilterOptionsResponse,
} from '@kirin/api-client/contracts/hooks';
import { HOOK_FILTER_OPTION_FIELDS } from '@kirin/api-client/contracts/hooks';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { type HookTabScope, hookScopeKey } from './scope';

/**
 * Fetch hooks with DataTable pagination, filters, and sorting — org or personal
 * scope (KIR-3643).
 */
export async function fetchHooks(
  scope: HookTabScope,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<HookInventoryRow>> {
  const listParams = {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field as HookDataTableSortField | undefined,
    sortOrder: params.sort?.order,
    search: params.search,
  };
  return scope.kind === 'organization'
    ? listHooksDataTable(apiClient, scope.organizationId, listParams, {
        signal,
      })
    : listAccountHooksDataTable(apiClient, listParams, { signal });
}

function fetchFilterOptions(
  scope: HookTabScope,
  field: HookFilterOptionField,
  params: FilterOptionsParams
): Promise<HookFilterOptionsResponse> {
  const query = {
    field,
    search: params.search,
    limit: params.limit ?? 10,
    offset: params.offset ?? 0,
  };
  return scope.kind === 'organization'
    ? getHookFilterOptions(apiClient, scope.organizationId, query)
    : getAccountHookFilterOptions(apiClient, query);
}

/**
 * Fetch distinct filter-option values for a server-backed hook field
 * (family, platform, file, projects). Powers the column-filter dropdowns.
 */
export async function fetchHookFilterOptions(
  scope: HookTabScope,
  field: HookFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await fetchFilterOptions(scope, field, params);
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for server-backed hook filter options. */
export function getHookFilterOptionsQueryKey(
  scope: HookTabScope,
  field: HookFilterOptionField,
  params: { search?: string; limit?: number; offset?: number }
): readonly unknown[] {
  return queryKeys.hooks.filterOptions(hookScopeKey(scope), field, params);
}

/**
 * Fetch Project entities (KIR-3356) for the Projects column filter.
 * Server-backed — returns only projects that own at least one hook.
 * Option value = project slug (stable filter key); label = project name.
 */
export async function fetchHookProjectFilterOptions(
  scope: HookTabScope,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await fetchFilterOptions(
    scope,
    HOOK_FILTER_OPTION_FIELDS.PROJECTS,
    params
  );

  const options = response.optionsWithMetadata
    ? response.optionsWithMetadata.map(o => ({
        value: o.value,
        label: o.label,
      }))
    : response.options.map(o => ({ value: o, label: o }));

  return {
    options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}

/** Query key for project filter options (server-backed). */
export function getHookProjectFilterOptionsQueryKey(
  scope: HookTabScope,
  params: FilterOptionsParams
): readonly unknown[] {
  return queryKeys.hooks.filterOptions(
    hookScopeKey(scope),
    HOOK_FILTER_OPTION_FIELDS.PROJECTS,
    params
  );
}
